import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagsService } from '@atlassian/jira-flags';
import type { main_homeBroadcastDismiss_Mutation as HomeBroadCastDismissMutationType } from '@atlassian/jira-relay/src/__generated__/main_homeBroadcastDismiss_Mutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { getErrorFlagOnDismiss } from '../../common/ui/flags';
import {
	TEAM_NAME,
	PACKAGE_NAME,
	ON_DISMISS_ERROR,
	ON_DISMISS_MUTATION_ERROR,
} from '../../constants';

export const useDismissBroadcast = () => {
	const cloudId = useCloudId();
	const { showFlag } = useFlagsService();

	const [commit, isInFlight] = useMutation<HomeBroadCastDismissMutationType>(graphql`
		mutation main_homeBroadcastDismiss_Mutation($cloudId: ID!, $id: ID!) {
			jira @optIn(to: ["JiraBroadcastMessage"]) {
				setUserBroadcastMessageDismissed(cloudId: $cloudId, id: $id) {
					userBroadcastMessage {
						id
						shouldDisplay
						isDismissed
					}
					success
					errors {
						extensions {
							statusCode
						}
						message
					}
				}
			}
		}
	`);

	const dismiss = useCallback(
		(id: string) => {
			commit({
				variables: { cloudId, id },
				onCompleted: (data) => {
					const isSuccess = Boolean(data.jira?.setUserBroadcastMessageDismissed?.success);
					// We only care about first error
					const error = (data.jira?.setUserBroadcastMessageDismissed?.errors ?? [])[0];
					if (!isSuccess || error) {
						const errorMessage = `error on dismiss jira home broadcast widget: ${error?.message}`;
						const statusCode = error?.extensions?.statusCode;

						fireErrorAnalytics({
							error: new Error(`status code: ${statusCode ?? 'unknown'}, ${errorMessage}`),
							meta: {
								id: ON_DISMISS_ERROR,
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
						});

						showFlag(getErrorFlagOnDismiss());
					}
				},
				onError: (error: Error) => {
					fireErrorAnalytics({
						error: new Error(`${ON_DISMISS_MUTATION_ERROR} with ${error.message}`),
						meta: {
							id: ON_DISMISS_MUTATION_ERROR,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
					});

					showFlag(getErrorFlagOnDismiss());
				},
			});
		},
		[cloudId, commit, showFlag],
	);

	return {
		dismiss,
		isDismissLoading: isInFlight,
	};
};
